import {TypenameOmit} from "@/tools";
import {
    ServiceDomainAllocationQuery,
    ServiceDomainAllocationSuperuserQuery,
    useServiceDomainAllocationQuery,
    useServiceDomainAllocationSuperuserQuery,
} from "../queries.generated";
import {DeepPartial} from "@apollo/client/utilities";
import {useDomainNameQuery} from "@/queries.generated";
import {useAuth} from "@/context/AuthProvider";
import {useCallback} from "react";

export type ServicesDataSourceData = TypenameOmit<
    ServiceDomainAllocationQuery["domainAllocation"]["services"] &
        DeepPartial<ServiceDomainAllocationSuperuserQuery["domainAllocation"]["services"]>
>;

export type ServicesDataSource = {
    loading: boolean;
    refetch: () => Promise<ServicesDataSourceData>;
    domainName: string;
    data: ServicesDataSourceData;
};

export const useServicesDataSource = (domainId: number) => {
    const auth = useAuth();
    const isSuperuser = auth.isSuperuser;
    const allocationQuery = isSuperuser ? useServiceDomainAllocationSuperuserQuery : useServiceDomainAllocationQuery;

    const domainNameQueryResult = useDomainNameQuery({
        variables: {id: domainId},
    });

    const allocationQueryResult = allocationQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const refetch = useCallback(async () => {
        const newData = await allocationQueryResult.refetch();
        return newData.data.domainAllocation.services as ServicesDataSourceData;
    }, [allocationQueryResult]);

    if (domainNameQueryResult.loading || allocationQueryResult.loading) {
        return {
            loading: true,
            refetch,
            domainName: undefined,
            data: undefined,
        };
    }

    const domainName = domainNameQueryResult.data.domains.items[0].name;
    const data: ServicesDataSourceData = allocationQueryResult.data.domainAllocation.services;

    return {
        loading: false,
        refetch,
        domainName,
        data,
    } as ServicesDataSource;
};
